import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { FunctionRegistryService } from '../function-registry/function-registry.service';
import { ComponentDataSourceService } from '../component-data-source/component-data-source.service';

// Import data destinations
import { add_subtype } from './data/add-subtype';
import { add_event } from './data/add-event';
import { add_goal } from './data/add-goal';
import { add_module } from './data/add-module';
import { updateModule } from './data/update-module';
import { updateSubtype } from './data/update-subtype';
import { addModuleVersion } from './data/add-module-version';
import { updateModuleVersion } from './data/update-module-version';
import { deleteModuleVersion } from './data/delete-module-version';
import { deleteModule } from './data/delete-module';
import { addSubtypeVersion } from './data/add-subtype-version';
import { updateSubtypeVersion } from './data/update-subtype-version';
import { deleteSubtypeVersion } from './data/delete-subtype-version';
import { deleteSubtype } from './data/delete-subtype';
import { addEducationalPath } from './data/add-educational-path';
import { put_educational_path_requirement } from './data/put-educational-path-requirement';
import { put_educational_path_maintenance } from './data/put-educational-path-maintenance';
import { add_rls_policy } from './data/add-rls-policy';
import { add_location } from './data/add-location';
import { add_room } from './data/add-room';
import { update_location } from './data/update-location';
import { update_room } from './data/update-room';
import { delete_location } from './data/delete-location';
import { delete_room } from './data/delete-room';
import { update_event_participation } from './data/update-event-participation';
import { update_event } from './data/update-event';
import { delete_event } from './data/delete-event';
import { unlink_event_from_group } from './data/unlink-event-from-group';
import { join_event } from './data/join-event';
import { update_event_users } from './data/update-event-users';
import { create_instructor_invitation } from './data/event/create-instructor-invitation';
import { save_instructor_invitation_assignments } from './data/event/save-instructor-invitation-assignments';
import { complete_instructor_invitation } from './data/event/complete-instructor-invitation';
import { update_event_extra } from './data/event/update-event-extra';
import { add_path_version } from './data/add-path-version';
import { update_path_version } from './data/update-path-version';
import { add_path_users } from './data/add-path-users';
import { update_path_user } from './data/update-path-user';
import { profile_user_ep_assign } from './data/profile/profile_user_ep_assign';
import { log_contact_form } from './data/log-contact-form';
import { add_template } from './data/add-template';
import { update_template } from './data/update-template';
import { delete_template } from './data/delete-template';
import { update_org_settings } from './data/update-org-settings';
import { apply_group_calendar_rotation } from './data/apply-group-calendar-rotation';
import { apply_group_calendar_week_rotation } from './data/apply-group-calendar-week-rotation';

// Import advisory data destinations
import { addAdvisory } from './data/advi/add-advisory';
import { updateAdvisory } from './data/advi/update-advisory';
import { toggleAdvisory } from './data/advi/toggle-advisory';
import { deleteAdvisory } from './data/advi/delete-advisory';
import { sendAdvisoryNow } from './data/advi/send-advisory-now';

@Injectable({
  providedIn: 'root'
})
export class ComponentDataDestService {
  private dataDestRegistry = new Map<string, Function>();
  private registeredDataDest: string[] = [];

  constructor(
    public http: HttpClient,
    public authService: AuthService,
    private functionRegistryService: FunctionRegistryService,
    private componentDataSourceService: ComponentDataSourceService,
    public injector: Injector
  ) {
    // Register built-in data destinations
    this.registerDataDest('add_subtype', (data: any) => add_subtype(this, componentDataSourceService, data));

    // Register new event system destinations
    this.registerDataDest('add_event', (data: any) => add_event(this, componentDataSourceService, data));
    this.registerDataDest('add_goal', (data: any) => add_goal(this, componentDataSourceService, data));
    this.registerDataDest('add_module', (data: any) => add_module(this, componentDataSourceService, data));
    this.registerDataDest('update_module', (data: any) => updateModule(componentDataSourceService).execute(data));
    this.registerDataDest('update_subtype', (data: any) => updateSubtype(componentDataSourceService).execute(data));
    this.registerDataDest('add_module_version', (data: any) => addModuleVersion(componentDataSourceService).execute(data));
    this.registerDataDest('update_module_version', (data: any) => updateModuleVersion(componentDataSourceService).execute(data));
    this.registerDataDest('delete_module_version', (data: any) => deleteModuleVersion(componentDataSourceService).execute(data));
    this.registerDataDest('delete_module', (data: any) => deleteModule(componentDataSourceService).execute(data));
    this.registerDataDest('add_subtype_version', (data: any) => addSubtypeVersion(componentDataSourceService).execute(data));
    this.registerDataDest('update_subtype_version', (data: any) => updateSubtypeVersion(componentDataSourceService).execute(data));
    this.registerDataDest('delete_subtype_version', (data: any) => deleteSubtypeVersion(componentDataSourceService).execute(data));
    this.registerDataDest('delete_subtype', (data: any) => deleteSubtype(componentDataSourceService).execute(data));
    this.registerDataDest('add_educational_path', (data: any) => addEducationalPath(this));
    this.registerDataDest('put_educational_path_requirement', (data: any) => put_educational_path_requirement(this, componentDataSourceService, data));
    this.registerDataDest('put_educational_path_maintenance', (data: any) => put_educational_path_maintenance(this, componentDataSourceService, data));
    this.registerDataDest('add_rls_policy', (data: any) => add_rls_policy(this, componentDataSourceService, data));
    this.registerDataDest('add_location', (data: any) => add_location(this, componentDataSourceService, data));
    this.registerDataDest('add_room', (data: any) => add_room(this, componentDataSourceService, data));
    this.registerDataDest('update_location', (data: any) => update_location(this, componentDataSourceService, data));
    this.registerDataDest('update_room', (data: any) => update_room(this, componentDataSourceService, data));
    this.registerDataDest('delete_location', (data: any) => delete_location(this, componentDataSourceService, data));
    this.registerDataDest('delete_room', (data: any) => delete_room(this, componentDataSourceService, data));

    // Register event participation destinations
    this.registerDataDest('update_event_participation', (data: any) => update_event_participation(this, componentDataSourceService, data));
    this.registerDataDest('update_event', (data: any) => update_event(this, componentDataSourceService, data));
    this.registerDataDest('delete_event', (data: any) => delete_event(this, data));
    this.registerDataDest('unlink_event_from_group', (data: any) => unlink_event_from_group(this, data));
    this.registerDataDest('join_event', (data: any) => join_event(this, componentDataSourceService, data));
    this.registerDataDest('update_event_users', (data: any) => update_event_users(this, componentDataSourceService, data));
    this.registerDataDest('create_instructor_invitation', (data: any) => create_instructor_invitation(this, componentDataSourceService, data));
    this.registerDataDest('save_instructor_invitation_assignments', (data: any) => save_instructor_invitation_assignments(this, componentDataSourceService, data));
    this.registerDataDest('complete_instructor_invitation', (data: any) => complete_instructor_invitation(this, componentDataSourceService, data));
    this.registerDataDest('update_event_extra', (data: any) => update_event_extra(this, componentDataSourceService, data));
    
    // Register path version destination
    this.registerDataDest('add_path_version', (data: any) => add_path_version(this, componentDataSourceService, data));
    this.registerDataDest('update_path_version', (data: any) => update_path_version(this, componentDataSourceService, data));
    this.registerDataDest('add_path_users', (data: any) => add_path_users(this, componentDataSourceService, data));
    this.registerDataDest('update_path_user', (data: any) => update_path_user(this, componentDataSourceService, data));
    this.registerDataDest('profile_user_ep_assign', (data: any) => profile_user_ep_assign(this, data));
    
    // Register contact form destination
    this.registerDataDest('log_contact_form', (data: any) => log_contact_form(this, componentDataSourceService, data));
    
    // Register template destinations
    this.registerDataDest('add_template', (data: any) => add_template(this, componentDataSourceService, data));
    this.registerDataDest('update_template', (data: any) => update_template(this, componentDataSourceService, data));
    this.registerDataDest('delete_template', (data: any) => delete_template(this, componentDataSourceService, data));

    // Register organization settings destination
    this.registerDataDest('update_org_settings', (data: any) => update_org_settings(this, componentDataSourceService, data));
    this.registerDataDest('apply_group_calendar_rotation', (data: any) => apply_group_calendar_rotation(this, data));
    this.registerDataDest('apply_group_calendar_week_rotation', (data: any) => apply_group_calendar_week_rotation(this, data));

    // Register advisory data destinations
    this.registerDataDest('add_advisory', (data: any) => addAdvisory(this, componentDataSourceService)(data));
    this.registerDataDest('update_advisory', (data: any) => updateAdvisory(this, componentDataSourceService)(data));
    this.registerDataDest('toggle_advisory', (data: any) => toggleAdvisory(this, componentDataSourceService)(data));
    this.registerDataDest('delete_advisory', (data: any) => deleteAdvisory(this, componentDataSourceService)(data));
    this.registerDataDest('send_advisory_now', (data: any) => sendAdvisoryNow(this, componentDataSourceService)(data));

  }

  registerDataDest(key: string, dataDest: Function): void {
    this.dataDestRegistry.set(key, dataDest);
    this.registeredDataDest.push(key);
  }

  getDataDest(key: string): Function | undefined {
    return this.dataDestRegistry.get(key);
  }
}
